import { ReactElement, useEffect, useState } from 'react';
import { chunk } from 'lodash';
import {
  Flow1Container,
  OptionButton,
  OptionButtonText,
  QuestionDiv,
  QuestionNavBtn
} from '@components/questionnaire/flow-1-components';
import { FormValue, Question, QuestionOption } from '@app/types';

interface Flow1Props {
  group: {
    options: Question[];
  };
  values: Record<string, FormValue>;
  setFieldValue: (key: string, value: string) => void;
  errors: Record<string, boolean>;
}

const CONFIG = {
  QUESTIONS_AREA: window.innerHeight - 440,
  QUESTION_AREA: 96,
  ANIMATION_DELAY: 650,
  ANSWER_DELAY: 300
};

export const Flow1 = ({
  group,
  values,
  setFieldValue,
  errors
}: Flow1Props): ReactElement | null => {
  const [parts, setParts] = useState<Question[][]>([]);
  const [activePart, setActivePart] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const isMobile = window.innerWidth < 800;

  useEffect(() => {
    const maxParts = Math.floor(CONFIG.QUESTIONS_AREA / CONFIG.QUESTION_AREA);
    let splitPart = maxParts;

    if (group.options?.[0]?.size) {
      splitPart = Math.floor(
        (CONFIG.QUESTIONS_AREA - group.options[0].size) / CONFIG.QUESTION_AREA
      );
    }

    const splitParts = chunk(group.options, isMobile ? 1 : splitPart);
    setParts(splitParts);
  }, [group.options, isMobile]);

  const onAnswer = (key: string, value: string) => {
    setFieldValue(key, value);

    if (questionsAnswered({ [key]: value })) {
      setTimeout(() => {
        if (activePart + 1 < parts.length) {
          setIsChanging(true);
          setTimeout(() => {
            setActivePart(activePart + 1);
            setIsChanging(false);
          }, CONFIG.ANIMATION_DELAY);
        }
      }, CONFIG.ANSWER_DELAY);
    }
  };

  const Option: React.FC<{ questionKey: string; option: QuestionOption }> = ({
    questionKey,
    option
  }) => {
    return (
      <OptionButton
        isActive={(values[questionKey] && values[questionKey] === option.key) as boolean}
        isMobile={isMobile}
        onClick={() => onAnswer(questionKey, option.key)}
      >
        <div className={'radio'} />
        <OptionButtonText
          isActive={(values[questionKey] && values[questionKey] === option.key) as boolean}
        >
          {option.label}
        </OptionButtonText>
      </OptionButton>
    );
  };

  const Question: React.FC<{ question: Question }> = ({ question }) => {
    return (
      <QuestionDiv
        isActive={values[question.key] as boolean}
        isMobile={isMobile}
        error={errors[question.key]}
      >
        <div className={'titleContainer'}>
          <p>{question.title}</p>
        </div>
        <div className={'optionsContainer'}>
          {question.options?.map((option) => {
            return (
              <Option
                key={`${question.key}_${option.key}`}
                questionKey={question.key}
                option={option}
              />
            );
          })}
        </div>
      </QuestionDiv>
    );
  };

  const questionsAnswered = (currentAnswer?: Record<string, string>) => {
    let answers = Object.assign(values || {}, currentAnswer || {});
    return Boolean(parts?.[activePart]?.every((currentQuestion) => answers?.[currentQuestion.key]));
  };

  const QuestionNavigator: React.FC = () => {
    return (
      <div className={'questionNavigatorContainer'}>
        <QuestionNavBtn
          disabled={activePart === 0}
          isMobile={isMobile}
          onClick={() => {
            if (activePart > 0) {
              setIsChanging(true);
              setTimeout(() => {
                setActivePart(activePart - 1);
                setIsChanging(false);
              }, CONFIG.ANIMATION_DELAY);
            }
          }}
        >
          <img
            className={'paginationBtnIcon'}
            alt={'up'}
            src={require('./../../assets/icons/up-icon.png')}
          />
        </QuestionNavBtn>
        <div className={'paginationText'}>
          <span className={'paginationFirst'}>{activePart + 1}</span>
          <span className={'paginationSlash'}>/</span>
          <span className={'paginationLast'}>{parts?.length}</span>
        </div>
        <QuestionNavBtn
          isActive={questionsAnswered()}
          disabled={activePart + 1 >= parts.length || !questionsAnswered()}
          isMobile={isMobile}
          onClick={() => {
            if (activePart + 1 < parts.length) {
              setIsChanging(true);
              setTimeout(() => {
                setActivePart(activePart + 1);
                setIsChanging(false);
              }, CONFIG.ANIMATION_DELAY);
            }
          }}
        >
          <img
            className={'paginationBtnIcon'}
            alt={'down'}
            src={require('./../../assets/icons/down-icon.png')}
          />
        </QuestionNavBtn>
      </div>
    );
  };

  const Separator: React.FC<{ question: Question }> = ({ question }) => {
    return (
      <div className={'separatorContainer'}>
        <p className={'separatorText'}>{question.title}</p>
      </div>
    );
  };

  if (!parts || !parts.length) {
    return null;
  }

  return (
    <Flow1Container
      isMobile={isMobile}
      fixedHeight={
        !(
          parts[activePart]?.[0]?.height === 'auto' ||
          parts[activePart]?.[0]?.size ||
          parts[activePart]?.[0]?.type === 'flow_1_desc_separator'
        )
      }
    >
      <div
        className={`questionsContainer ${parts.length > 1 ? (isChanging ? 'questionsOut' : 'questionsIn') : ''}`}
      >
        {parts[activePart].map((question) => {
          if (question.type === 'flow_1_desc_separator') {
            return <Separator key={question.key} question={question} />;
          } else {
            return <Question key={question.key} question={question} />;
          }
        })}
      </div>
      {parts.length > 1 && <QuestionNavigator />}
    </Flow1Container>
  );
};
